import * as R from 'ramda'
import { useCallback, memo } from 'react'
import {
  IMessage,
  MessageSender,
  RawFileMessage,
  RawAudioMessage,
  RawVoiceMessage,
  RawImageMessage,
  RawVideoMessage,
  RawWappMessage,
  RawContactMessage,
  CallMessageAttachment,
  WappMessage,
} from '@weblite/ts-action/message'
import { Reply } from '$common/reply'
import { PendingMessage } from '$logic/pending-messages/types'
import { PendingUpload } from '$logic/pending-uploads/types'
import { ViewCountableMessage } from '$logic/message'
import { SenderInfo } from './components/sender-info'
import { TimeAndStatus } from './components/time-and-status'
import { Text } from './message-bodies/text'
import { Audio } from './message-bodies/audio'
import { FileComponenet } from './message-bodies/file'
import { Voice } from './message-bodies/voice'
import { Image } from './message-bodies/image'
import { Video } from './message-bodies/video'
import { Wapp } from './message-bodies/wapp'
import { Contact } from './message-bodies/contact'
import { Call } from './message-bodies/call'

function MessageSelectorComponent({
  message,
  canShowStatus,
  canShowSenderName,
  isInSelectionMode,
  shouldGetVideoHighQualityThumbnail,
}: {
  message: IMessage & { uploadId?: string } & PendingMessage &
    PendingUpload &
    ViewCountableMessage
  canShowStatus: boolean
  canShowSenderName: boolean
  isInSelectionMode?: boolean
  shouldGetVideoHighQualityThumbnail?: boolean
}) {
  const {
    iid,
    type,
    sender,
    body,
    forwardedFrom,
    repliedTo,
    date,
    patches,
    attachment,
    roomId,
    uploadId,
    pendingDate,
    viewCount,
    pinned,
    links,
    thumbnailUploadId,
    id,
  } = message

  const hasSenderInfo = Boolean(canShowSenderName || forwardedFrom)
  const getStatusComp = useCallback(
    (position: 'absolute' | 'unset' = 'absolute') => (
      <TimeAndStatus
        iid={iid}
        time={date || pendingDate ? new Date(date || pendingDate) : undefined}
        edited={patches?.edited}
        pinned={Boolean(pinned)}
        canShowStatus={canShowStatus}
        overrideStyle={{ position }}
        viewCount={viewCount}
      />
    ),
    [canShowStatus, patches, viewCount, pinned, iid],
  )

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        pointerEvents: isInSelectionMode ? 'none' : 'initial',
      }}
    >
      {hasSenderInfo && (
        <SenderInfo
          sender={sender as MessageSender}
          forwardedFrom={forwardedFrom as MessageSender}
        />
      )}

      {repliedTo && (
        <div
          style={{
            boxSizing: 'border-box',
            padding: '8px 10px 0',
          }}
        >
          <Reply roomId={roomId} messageIid={repliedTo} />
        </div>
      )}

      {type === 'FILE' ? (
        <FileComponenet
          attachment={attachment as RawFileMessage['attachment']}
          statusComp={!body ? getStatusComp() : null}
          uploadId={uploadId}
          rootStyle={{
            paddingTop: hasSenderInfo ? '8px' : '10px',
            paddingBottom: body ? 0 : '25px',
          }}
          iid={iid}
          roomId={roomId}
          id={id}
        />
      ) : type === 'AUDIO' ? (
        <Audio
          attachment={attachment as RawAudioMessage['attachment']}
          statusComp={!body ? getStatusComp() : null}
          uploadId={uploadId}
          rootStyle={{
            paddingTop: hasSenderInfo ? '8px' : '10px',
            paddingBottom: body ? 0 : '25px',
          }}
          iid={iid}
          roomId={roomId}
        />
      ) : type === 'VOICE' ? (
        <Voice
          attachment={attachment as RawVoiceMessage['attachment']}
          statusComp={!body ? getStatusComp() : null}
          uploadId={uploadId}
          rootStyle={{
            paddingTop: hasSenderInfo ? '8px' : '10px',
            paddingBottom: body ? 0 : '25px',
          }}
          iid={iid}
          roomId={roomId}
          sender={sender as MessageSender}
        />
      ) : type === 'IMAGE' ? (
        <Image
          attachment={attachment as RawImageMessage['attachment']}
          statusComp={!body ? getStatusComp('unset') : null}
          uploadId={uploadId}
          rootStyle={{ marginTop: hasSenderInfo || repliedTo ? '8px' : 0 }}
          roomId={roomId}
          iid={iid}
          id={id}
        />
      ) : type === 'VIDEO' ? (
        <Video
          attachment={attachment as RawVideoMessage['attachment']}
          statusComp={!body ? getStatusComp('unset') : null}
          uploadId={uploadId}
          thumbnailUploadId={thumbnailUploadId}
          rootStyle={{ marginTop: hasSenderInfo || repliedTo ? '8px' : 0 }}
          roomId={roomId}
          iid={iid}
          shouldGetHighQualityThumbnail={shouldGetVideoHighQualityThumbnail}
          id={id}
        />
      ) : type === 'WAPP' ? (
        <Wapp
          /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          // @ts-ignore
          attachment={attachment as RawWappMessage['attachment']}
          statusComp={!body ? getStatusComp('unset') : null}
          rootStyle={{
            margin: '8px 10px',
            borderRadius: '4px',
          }}
          messageObj={message as WappMessage}
        />
      ) : type === 'CONTACT' ? (
        <Contact
          /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          // @ts-ignore
          attachment={attachment as RawContactMessage['attachment']}
          rootStyle={{ marginTop: hasSenderInfo || repliedTo ? '8px' : 0 }}
          statusComp={!body ? getStatusComp() : null}
          iid={iid}
          roomId={roomId}
        />
      ) : type === 'CALL' ? (
        <Call
          /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          // @ts-ignore
          attachment={attachment as CallMessageAttachment}
          statusComp={!body ? getStatusComp() : null}
          sender={sender}
          roomId={roomId}
          iid={iid}
        />
      ) : null}

      {body && (
        <Text
          text={body}
          statusComp={getStatusComp('unset') as JSX.Element}
          rootStyle={
            type !== 'TEXT' || hasSenderInfo ? { paddingTop: '8px' } : undefined
          }
          links={links}
        />
      )}
    </div>
  )
}

MessageSelectorComponent.defaultProps = {
  isInSelectionMode: false,
}

export const MessageSelector = memo(
  MessageSelectorComponent,
  (oldProps, newProps) => R.equals(oldProps, newProps),
)
